import axios from 'axios';
import qs from 'qs';
import Config from '../config/env_variables';
import PostMessageUtil from '../utils/PostMessageUtil';
import { genLogger } from '../lib/logger';
import { loggerNames } from '../utils/constants';

const { error } = genLogger(loggerNames.services.BACKEND);

/*
*   Validate User Token through backend API
*   @param {string} accessToken - User token
*   @return {Promise} a promise which will resolve with the response
*/
const validateToken = (accessToken) => {
  const config = {
    method: 'post',
    mode: 'cors',
    // url: `${process.env.REACT_APP_API_BASE_URL}/validateAccessToken,
    url: `${Config.REACT_APP_API_BASE_URL}/validateAccessToken`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-api-key': Config.REACT_APP_CLIENT_ID,
      Authorization: `Bearer ${accessToken}`,
    },
    data: qs.stringify({ accessToken }),
  };

  const promise = axios(config);

  const dataPromise = promise
    .then((response) => response.data)
    .catch((err) => err);

  return dataPromise;
};

/*
*   Validate User Token through backend API
*   @param {string} rengaId - User GUID
*   @param {string} orgId - Org GUID
*   @param {string} language - User language info
*   @return {Promise} a promise which will resolve with the response
*/
const getEntitledProducts = (accessToken, rengaId, language, orgId, email, requestOrigin) => {
  const config = {
    method: 'post',
    mode: 'cors',
    url: `${Config.REACT_APP_API_BASE_URL}/getEntitledProductList`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-api-key': Config.REACT_APP_CLIENT_ID,
      Authorization: `Bearer ${accessToken}`,
    },
    data: qs.stringify({
      accessToken, rengaId, language, orgId, email, requestOrigin,
    }),
  };

  const promise = axios(config);

  const dataPromise = promise.then((response) => response.data);

  return dataPromise;
};

const getEntitledProduct = (accessToken, rengaId, language, orgId, email, requestOrigin, productType) => {
  const config = {
    method: 'post',
    mode: 'cors',
    url: `${Config.REACT_APP_API_BASE_URL}/getEntitledProduct`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-api-key': Config.REACT_APP_CLIENT_ID,
      Authorization: `Bearer ${accessToken}`,
    },
    data: qs.stringify({
      accessToken, rengaId, language, orgId, email, requestOrigin, productType
    }),
  };

  const promise = axios(config);

  const dataPromise = promise.then((response) => response.data);

  return dataPromise;
};


const getMarketInstance = (accessToken, orgId, email) => {
  const config = {
    method: 'post',
    mode: 'cors',
    url: `${Config.REACT_APP_API_BASE_URL}/getMarketoInstances`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-api-key': Config.REACT_APP_CLIENT_ID,
    },
    data: qs.stringify({
      accessToken, orgId, email,
    }),
  };

  const promise = axios(config);

  const dataPromise = promise.then((response) => response.data);

  return dataPromise;
};

const fetchFileHandlerAWS = async (accessToken, conversationId, sessionId, messages, timezone) => {
  try {
    const config = {
      method: 'post',
      mode: 'cors',
      url: `${Config.REACT_APP_API_BASE_URL}/getChatTranscript`,
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-api-key': Config.REACT_APP_CLIENT_ID,
        Authorization: `Bearer ${accessToken}`,
      },
      data: JSON.stringify({ messages, conversationId, sessionId, timezone }),
      responseType: 'blob',
    };
    const response = await axios(config);
    const contentDisposition = response.headers['content-disposition'];
    const fileName = contentDisposition.split(';')[1].split('=')[1];

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
     // Convert Blob to Base64 and send it to the parent window to download the file in Android or IOS
     sendFileToAppAsBase64(response.data, fileName);

  } catch (err) {
    error('Error fetching chat transcript:', err?.message || err);
  }
};
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result.split(',')[1]); // Extract Base64 part
      reader.onerror = (error) => reject(error);
  });
}
function sendFileToAppAsBase64(blob, fileName) {
  try {
    blobToBase64(blob).then((base64) => {
      PostMessageUtil.sendMessage("adbmsg_FILE_DOWNLOAD", { data: base64, fileName: fileName });
    }).catch((err) => error('Error converting file to base64:', err?.message || err));
  } catch (err) {
    error('Error in sending file to IOS/android App:', err?.message || err);
  }
}
const getUnAuthProductList = (language, requestOrigin, product) => {
  const config = {
    method: 'post',
    mode: 'cors',
    url: `${Config.REACT_APP_API_BASE_URL}/getUnAuthProductList`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-api-key': Config.REACT_APP_CLIENT_ID,
    },
    data: qs.stringify({
      language, requestOrigin, product,
    }),
  };

  const promise = axios(config);

  const dataPromise = promise.then((response) => response.data);

  return dataPromise;
};


//New API: POST /generateContactSession
// This API accepts a contactId and returns a sessionId.
// It should be called immediately once the chat starts (i.e., when the contact is created).
// Note: The API will only work within 1 minute of contact creation.
// This has been whitelisted in API Gateway.

const generateContactSession = ({contactId}) => {
  if (!contactId) {
    error('Contact ID is required');
    return Promise.reject(new Error('Contact ID is required'));
  }
  const config = {
    method: 'post',
    mode: 'cors',
    url: `${Config.REACT_APP_API_BASE_URL}/generateContactSession`,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-api-key': Config.REACT_APP_CLIENT_ID,
    },
    data: qs.stringify({ contactId }),
  };
  const promise = axios(config);
  const dataPromise = promise.then((response) => response.data);
  return dataPromise;
};

const BackendService = {
  validateToken,
  getEntitledProducts,
  getEntitledProduct,
  getUnAuthProductList,
  fetchFileHandlerAWS,
  getMarketInstance,
  sendFileToAppAsBase64,
  generateContactSession,
};

export default BackendService;
